import { useState, ChangeEvent, FocusEvent } from 'react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import IconWrapper from '@components/ui/Icons';
import styles from './InputField.module.scss';

export enum InputType {
  text = 'text',
  password = 'password',
  email = 'email',
  date = 'date',
  tel = 'tel',
  orderNumber = 'orderNumber',
}
export interface InputFieldProps {
  id: string;
  type: InputType;
  value?: string;
  onChange: (value: string) => void;
  onFocus?: (value: string) => void;
  onBlur?: (value: string) => void;
  classNames?: string | string[];
  hasError?: boolean | '';
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  minDate?: string;
  maxDate?: string;
  maxLength?: number;
  pattern?: string;
  autoComplete?: string;
  optional?: boolean;
  title?: string;
}

export const formatPhoneNumber = (phone?: string) => {
  if (!phone) return phone;
  const phoneNumber = phone.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  if (phoneNumberLength < 11) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
      3,
      6
    )}-${phoneNumber.slice(6, 10)}`;
  }
  return phoneNumber;
};

export const formatOrderNumberChange = (value: string) => {
  if (!value) {
    return '';
  }
  // Only keep digits
  const digits = value.replace(/[^\d]/g, '');
  if (!digits) {
    return 'SO-';
  }
  return `SO-${digits}`;
};

export const InputField = ({
  id,
  type,
  value,
  onChange,
  onFocus,
  onBlur,
  classNames,
  hasError,
  disabled,
  placeholder,
  ariaLabel,
  minDate,
  maxDate,
  maxLength,
  pattern,
  autoComplete,
  optional,
  title,
}: InputFieldProps) => {
  const { t } = useTranslation('common');
  const [showPassword, setShowPassword] = useState(false);
  const toggleShowPassword = () => setShowPassword((state) => !state);
  const inputType =
    type === InputType.password && showPassword ? InputType.text : type;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (inputType === InputType.tel) {
      const telephoneNumber = formatPhoneNumber(e.target.value);
      if (telephoneNumber || telephoneNumber === '') {
        onChange(telephoneNumber);
      }
    } else if (inputType === InputType.orderNumber) {
      const formatted = formatOrderNumberChange(e.target.value);
      if (formatted || formatted === '') {
        onChange(formatted);
      }
    } else if (onChange) onChange(e.target.value);
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (onFocus) onFocus(e.target.value);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (onBlur) onBlur(e.target.value);
  };

  const containerClasses = cn(styles.inputContainer, classNames, {
    [styles.isDisabled]: disabled,
    [styles.hasError]: hasError,
  });

  return (
    <div className={containerClasses}>
      <input
        id={id}
        type={inputType}
        onChange={handleChange}
        value={value}
        maxLength={inputType === InputType.tel ? 50 : maxLength || undefined}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onBlur={handleBlur}
        onFocus={handleFocus}
        min={minDate || undefined}
        max={maxDate || undefined}
        required={optional && undefined}
        pattern={pattern || undefined}
        autoComplete={autoComplete || undefined}
        title={title || undefined}
      />

      {type === InputType.password && (
        <button
          type="button"
          onClick={toggleShowPassword}
          aria-label={showPassword ? t('hide.password') : t('show.password')} // TODO: Confirm translations
        >
          {showPassword ? (
            <IconWrapper
              classNames={styles.icon}
              icon="hide"
              width={18}
              height={18}
              fill="var(--color-gray-dark)"
            />
          ) : (
            <IconWrapper
              classNames={styles.icon}
              icon="show"
              width={18}
              height={18}
              fill="var(--color-gray-dark)"
            />
          )}
        </button>
      )}
    </div>
  );
};

export default InputField;
